/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import { ia } from 'lib/helpers/utility';

import AGTable from '../AGTable/AGTable';
import TableFilters from '../AgTablePage/components/TableFilters';
import { DEFAULT_COLUMN_DEFS } from '../AgTablePage/lib/constants';
import { createQueryFilters } from '../AgTablePage/lib/createQueryFilters';
import getTableParams from '../AgTablePage/lib/getParams';

import { getClickedColId } from './lib/helpers';
import infiniteDatasource from './lib/infiniteDatasource';
import infiniteGridOptions from './lib/infiniteGridOptions';

const AGInfiniteTable = (props) => {
  const { defaultFilters = {}, defaultColumns, name, ...rest } = props;

  return (
    <TableContextProvider
      defaultFilters={defaultFilters}
      cols={defaultColumns}
      name={name}
      isInfinite
      {...rest}>
      <AGInfiniteTableContent {...props} />
    </TableContextProvider>
  );
};

const AGInfiniteTableContent = ({
  queryParams = {},
  limit = 100,
  skeletonSize: skeletonSizeProps,
  formatData,
  queryKey,
  modalView,
  disabledCells = [],
  queryFn,
  preSelectedIds,
  setSelectedRows,
  noDisplayButton,
  paramPrefix,
  customData,
  setFetchedData,
  presetFilters,
  gridOptions: propsGridOptions,
  ...props
}) => {
  const [searchParams] = useSearchParams();
  const persistedSelectedIdsRef = useRef(new Set());
  const selectAllAcrossPagesRef = useRef(false);
  const hasSetDataSourceRef = useRef(false);
  const skeletonSize = skeletonSizeProps || limit;

  const status = searchParams.get('status');
  const {
    name,
    filters,
    setFilters,
    defaultFilters,
    initialFilters,
    forceRefresh,
    isEditingFilter,
    selectedRows,
    gridApi,
    tableLoading,
    changeFilters,
    resetSortAndPage
  } = useTableContext();

  const { sortField: sort } = useMemo(
    () => getTableParams(searchParams, paramPrefix),
    [searchParams, paramPrefix]
  );

  const isSingleSelection = props?.gridOptions?.rowSelection === 'single';

  const hasCustomData = ia(customData);
  const hasServerData = typeof queryFn === 'function';

  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const totalCountRef = useRef(0);
  totalCountRef.current = totalCount;

  useEffect(() => {
    return () => {
      resetSortAndPage();
    };
  }, []);

  useEffect(() => {
    if (!setSelectedRows || !selectedRows) return;
    setSelectedRows(selectedRows);
  }, [selectedRows]);

  useEffect(() => {
    if (!gridApi) return;

    const onRowSelected = (event) => {
      const id = String(event?.node?.data?.id ?? event?.node?.id);
      if (!id) return;
      if (event.node.selected) {
        if (isSingleSelection) {
          persistedSelectedIdsRef.current = new Set([id]);
        } else {
          persistedSelectedIdsRef.current.add(id);
        }
      } else {
        persistedSelectedIdsRef.current.delete(id);
      }
    };

    gridApi.addEventListener('rowSelected', onRowSelected);
    return () => {
      gridApi.removeEventListener('rowSelected', onRowSelected);
    };
  }, [gridApi, isSingleSelection]);

  const queryFilters = useMemo(() => {
    const allFilters = { ...filters, ...(presetFilters || {}) };
    return createQueryFilters(allFilters, status);
  }, [filters, status, presetFilters]);

  const handleClickToToggle = useCallback(
    (event) => {
      const colDef = event?.column?.getColDef?.() || event?.colDef;
      const clickedColId = getClickedColId(event);
      const nativeEvent = event?.event || event?.e || event?.nativeEvent;
      const clickedCheckbox = nativeEvent?.target?.closest?.(
        '.ag-selection-checkbox, .ag-checkbox-input, input[type="checkbox"]'
      );
      const hasActionsClass = nativeEvent?.target?.closest('.actions') !== null;
      if (
        ['actions', ...disabledCells]?.includes(event?.column?.colId) ||
        ['actions', ...disabledCells]?.includes(clickedColId) ||
        event?.node?.group ||
        colDef?.checkboxSelection ||
        clickedCheckbox ||
        event?.node?.data?._isSkeleton ||
        hasActionsClass
      )
        return;
      const id = String(event?.node?.data?.id ?? event?.node?.id);
      if (!id) return;
      const willSelect = !event?.node?.selected;
      if (willSelect) {
        if (isSingleSelection) {
          if (gridApi) {
            gridApi.forEachNode((node) => {
              if (node?.selected && String(node?.data?.id ?? node?.id) !== id) {
                node.setSelected(false);
              }
            });
          }
          persistedSelectedIdsRef.current = new Set([id]);
        } else {
          persistedSelectedIdsRef.current.add(id);
        }
      } else {
        persistedSelectedIdsRef.current.delete(id);
      }
      event?.node.setSelected(willSelect);
    },
    [gridApi, isSingleSelection, disabledCells]
  );

  const applySelection = useCallback(() => {
    if (!gridApi) return;
    if (selectAllAcrossPagesRef.current && !isSingleSelection) {
      gridApi.forEachNode((node) => {
        if (!node || node.group || node?.data?._isSkeleton || node.selectable === false) return;
        const nodeId = String(node?.data?.id ?? node?.id);
        if (!nodeId) return;
        if (!node.selected) {
          node.setSelected(true);
        }
        persistedSelectedIdsRef.current.add(nodeId);
      });
      return;
    }
    let alreadySelectedOne = false;
    gridApi.forEachNode((node) => {
      const nodeId = String(node?.data?.id ?? node?.id);
      const isInSet = persistedSelectedIdsRef.current.has(nodeId);
      const shouldBeSelected = isSingleSelection ? isInSet && !alreadySelectedOne : isInSet;
      if (node.selected !== shouldBeSelected) {
        node.setSelected(shouldBeSelected);
      }
      if (isSingleSelection && shouldBeSelected) {
        alreadySelectedOne = true;
      }
    });
    if (isSingleSelection) {
      const firstKept = new Set();
      for (const value of persistedSelectedIdsRef.current.values()) {
        firstKept.add(value);
        break;
      }
      persistedSelectedIdsRef.current = firstKept;
    }
  }, [gridApi, isSingleSelection]);

  useEffect(() => {
    if (!gridApi) return;

    gridApi.setSelectAllAcrossPages = (enabled) => {
      selectAllAcrossPagesRef.current = Boolean(enabled);
      if (!enabled) {
        persistedSelectedIdsRef.current = new Set();
      }
      applySelection();
      gridApi.refreshHeader();
    };
    gridApi.isSelectAllAcrossPages = () => selectAllAcrossPagesRef.current;

    return () => {
      gridApi.setSelectAllAcrossPages = undefined;
      gridApi.isSelectAllAcrossPages = undefined;
    };
  }, [gridApi, applySelection]);

  useEffect(() => {
    if (ia(preSelectedIds)) {
      persistedSelectedIdsRef.current = new Set(preSelectedIds.map(String));
    } else {
      persistedSelectedIdsRef.current = new Set();
    }
    applySelection();
  }, [preSelectedIds, applySelection]);

  const dataSource = useMemo(
    () =>
      infiniteDatasource({
        isInfinite: hasServerData,
        hasCustomData,
        hasServerData,
        customData,
        queryFn,
        limit,
        skeletonSize,
        setLoading,
        setFetchedData,
        sort,
        queryFilters,
        queryParams,
        queryKey,
        name,
        formatData,
        setTotalCount,
        totalCountRef,
        selectPreselectedNodes: applySelection
      }),
    [
      hasCustomData,
      hasServerData,
      customData,
      setFetchedData,
      queryFn,
      limit,
      setLoading,
      sort,
      queryFilters,
      queryParams,
      queryKey,
      name,
      formatData,
      applySelection
    ]
  );

  const gridOptions = useMemo(
    () => ({
      ...(propsGridOptions || {}),
      ...infiniteGridOptions({ isInfinite: hasServerData, limit })
    }),
    [limit, hasServerData, propsGridOptions]
  );

  const datasourceKey = useMemo(
    () =>
      JSON.stringify({
        limit,
        sort,
        queryFilters,
        queryParams,
        queryKey,
        name
      }),
    [limit, sort, queryFilters, queryParams, queryKey, name]
  );

  useEffect(() => {
    if (!gridApi || !hasServerData || hasSetDataSourceRef.current) return;
    setLoading(true);
    gridApi.setDatasource(dataSource);
  }, [gridApi, hasServerData, datasourceKey]);

  useEffect(() => {
    if (!gridApi || !hasServerData) return;

    if (totalCount === 0 && !loading && !tableLoading) {
      gridApi.showNoRowsOverlay();
    } else if (totalCount > 0) {
      gridApi.hideOverlay();
    }
  }, [gridApi, hasServerData, totalCount, loading, tableLoading]);

  const basicTableData = useMemo(() => {
    if (!hasCustomData || hasServerData) return undefined;
    const arr = customData || [];
    return formatData ? formatData(arr) : arr;
  }, [hasCustomData, customData, formatData, hasServerData]);

  const infiniteProps = hasServerData
    ? { dataSource, rowModelType: 'infinite' }
    : { data: basicTableData };

  const userOnRowClicked = props?.onRowClicked;
  const userOnFirstDataRendered = props?.onFirstDataRendered;
  const userOnModelUpdated = props?.onModelUpdated;

  return (
    <>
      <TableFilters
        filters={filters}
        initialFilters={initialFilters}
        defaultFilters={defaultFilters}
        modalView={modalView}
        className="ml-2 rounded-lg !px-0"
        setFilters={setFilters}
        noDisplayButton={noDisplayButton}
        category={name}
        isEditingFilter={isEditingFilter}
        changeFilters={changeFilters}
      />
      <div className="relative flex h-full flex-col overflow-hidden">
        <div className={`ag-theme-quartz h-full${isSingleSelection ? ' ag-single-selection' : ''}`}>
          <AGTable
            key={forceRefresh}
            gridOptions={gridOptions}
            {...infiniteProps}
            defaultColDef={DEFAULT_COLUMN_DEFS}
            loading={loading || tableLoading}
            skeletonSize={skeletonSize}
            {...props}
            onRowClicked={(e) => {
              handleClickToToggle(e);
              if (typeof userOnRowClicked === 'function') userOnRowClicked(e);
            }}
            onFirstDataRendered={(e) => {
              applySelection();
              if (typeof userOnFirstDataRendered === 'function') userOnFirstDataRendered(e);
            }}
            onModelUpdated={(e) => {
              applySelection();
              if (typeof userOnModelUpdated === 'function') userOnModelUpdated(e);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default AGInfiniteTable;
